/* ========================================================================
   © 2026 FixMySiteKit Framework. All rights reserved.
   Directory Index — /solutions
   Hidden SEO hub listing all 16 verticals × 50 metros.
   ======================================================================== */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Zap } from "lucide-react";
import SiteFooter from "@/components/SiteFooter";
import DirectoryLoading from "@/components/DirectoryLoading";
import useSeoHead from "@/lib/useSeoHead";
import { API_BASE } from "@/lib/api";

const CANONICAL_HOST = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function DirectoryIndex() {
    const [state, setState] = useState({ loading: true, niches: [], total: 0 });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${API_BASE}/directory/niches`, { credentials: "omit" });
                const data = await res.json();
                if (!cancelled) {
                    setState({
                        loading: false,
                        niches: data.niches || [],
                        total: data.totalBranches || 0,
                        locations: data.totalLocations || 0,
                    });
                }
            } catch (_) {
                if (!cancelled) setState({ loading: false, niches: [], total: 0 });
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const canonical = `${CANONICAL_HOST}/solutions`;
    useSeoHead({
        title: "Local Service Directory — Verified Regional Branches | FixMySiteKit",
        description:
            "Browse the FixMySiteKit verified regional network — 16 core trade verticals with sub-30-second speed-to-lead dispatch across 50 US metros.",
        canonical,
        keywords: "speed to lead, local services, verified operators, regional dispatch, home service directory",
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "FixMySiteKit Verified Regional Branches",
            url: canonical,
            description:
                "Directory of FixMySiteKit-verified operator branches across the top 50 US metros.",
        },
    });

    if (state.loading) return <DirectoryLoading label="Loading verified branches…" />;

    return (
        <div
            data-testid="directory-index"
            className="min-h-screen bg-background text-foreground"
        >
            <header
                className="border-b border-border bg-background/85 backdrop-blur"
                role="banner"
            >
                <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-10">
                    <Link
                        to="/"
                        data-testid="directory-home-link"
                        className="inline-flex items-center gap-2"
                        aria-label="FixMySiteKit home"
                    >
                        <span className="grid h-8 w-8 place-items-center bg-primary text-primary-foreground">
                            <Zap className="h-4 w-4" aria-hidden="true" />
                        </span>
                        <span className="font-display text-lg font-bold tracking-tight">
                            FixMySiteKit
                        </span>
                    </Link>
                    <span className="font-mono text-xs uppercase tracking-[0.25em] text-primary">
                        Verified Directory
                    </span>
                </div>
            </header>

            <main role="main">
                <section
                    aria-labelledby="directory-heading"
                    className="border-b border-border"
                >
                    <div className="mx-auto max-w-7xl px-6 py-16 md:px-10 md:py-20">
                        <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">
                            Regional Network · {state.total || 800} Branches
                        </p>
                        <h1
                            id="directory-heading"
                            data-testid="directory-index-heading"
                            className="mt-3 max-w-3xl font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl"
                        >
                            Verified regional branches across{" "}
                            <span className="text-primary">
                                {state.locations || 50} US metros.
                            </span>
                        </h1>
                        <p className="mt-6 max-w-2xl text-base text-muted-foreground md:text-lg">
                            Every branch is wired to the FixMySiteKit speed-to-lead relay —
                            web-form submissions ring a live operator inside 30 seconds,
                            covering {state.niches?.length || 16} core service verticals.
                        </p>
                    </div>
                </section>

                <section
                    aria-labelledby="niche-grid-heading"
                    className="border-b border-border"
                >
                    <div className="mx-auto max-w-7xl px-6 py-14 md:px-10">
                        <h2
                            id="niche-grid-heading"
                            className="font-display text-2xl font-bold tracking-tight md:text-3xl"
                        >
                            Choose a service vertical
                        </h2>
                        <ul
                            className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
                            data-testid="directory-niche-grid"
                        >
                            {state.niches.map((n) => (
                                <li key={n.slug}>
                                    <Link
                                        to={`/solutions/${n.slug}`}
                                        data-testid={`niche-card-${n.slug}`}
                                        className="group block h-full border border-border bg-card p-6 transition-colors hover:border-primary/60 hover:bg-primary/5"
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div>
                                                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-primary">
                                                    {n.sla} min SLA
                                                </p>
                                                <h3 className="mt-2 font-display text-lg font-bold tracking-tight">
                                                    {n.label}
                                                </h3>
                                            </div>
                                            <ArrowRight
                                                className="h-5 w-5 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary"
                                                aria-hidden="true"
                                            />
                                        </div>
                                        <p className="mt-3 text-sm text-muted-foreground">
                                            Dispatching for {n.problem}.
                                        </p>
                                        <p className="mt-4 inline-flex items-center gap-2 text-xs text-muted-foreground">
                                            <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                                            {n.locationCount} metros
                                        </p>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>

                <section className="border-b border-border">
                    <div className="mx-auto max-w-7xl px-6 py-14 md:px-10">
                        <div className="border border-border bg-card p-8 md:p-10">
                            <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">
                                Own the phone that rings
                            </p>
                            <h2 className="mt-3 max-w-2xl font-display text-2xl font-bold tracking-tight md:text-3xl">
                                Plug your existing web form into the same speed-to-lead relay.
                            </h2>
                            <Link
                                to="/"
                                data-testid="directory-cta-funnel"
                                className="mt-6 inline-flex items-center gap-2 bg-primary px-5 py-3 font-mono text-xs font-bold uppercase tracking-[0.25em] text-primary-foreground transition-transform hover:-translate-y-0.5"
                            >
                                Activate my branch
                                <ArrowRight className="h-4 w-4" aria-hidden="true" />
                            </Link>
                        </div>
                    </div>
                </section>
            </main>

            <SiteFooter />
        </div>
    );
}
